/**
 * Real-time Firestore sync hooks for onboarding
 *
 * These hooks replace HTTP polling with Firestore real-time listeners,
 * providing instant updates when backend state changes.
 */

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { doc, onSnapshot } from 'firebase/firestore'
import { getFirestoreDb } from '../firebase'

/**
 * Listen to real-time onboarding progress updates
 *
 * Replaces polling with Firestore listener. Updates query cache automatically
 * when backend writes progress changes.
 *
 * @param tenantId - Tenant ID to listen to
 * @param enabled - Whether to enable the listener (default: true)
 */
export function useRealtimeOnboardingProgress(tenantId: string | null | undefined, enabled = true) {
  const queryClient = useQueryClient()

  useEffect(() => {
    const db = getFirestoreDb()
    if (!tenantId || !enabled || !db) return

    const docRef = doc(db, 'tenants', tenantId, 'realtime', 'onboarding_progress')

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()

          // Update the query cache with fresh data
          queryClient.setQueryData(['onboarding-progress'], {
            tenant_id: tenantId,
            status: data.status,
            current_step: data.current_step,
            completed_steps: (data.completed_steps as Record<string, unknown> | undefined) || {},
            progress_percent: data.progress_percent || 0,
            is_complete: data.is_complete || false,
            is_admin_setup: data.is_admin_setup || false,
          })

          console.log('[Realtime] Onboarding progress updated:', data.status, data.current_step)
        }
      },
      (error) => {
        console.error('[Realtime] Error listening to onboarding progress:', error)
      }
    )

    return () => {
      console.log('[Realtime] Unsubscribed from onboarding progress')
      unsubscribe()
    }
  }, [tenantId, enabled, queryClient])
}

/**
 * Listen to real-time WhatsApp config updates
 *
 * @param tenantId - Tenant ID to listen to
 * @param enabled - Whether to enable the listener (default: true)
 */
export function useRealtimeWhatsAppConfig(tenantId: string | null | undefined, enabled = true) {
  const queryClient = useQueryClient()

  useEffect(() => {
    const db = getFirestoreDb()
    if (!tenantId || !enabled || !db) return

    const docRef = doc(db, 'tenants', tenantId, 'realtime', 'whatsapp_config')

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()

          // Update the query cache
          queryClient.setQueryData(['whatsapp-status'], {
            setup_status: data.setup_status,
            phone_number: data.phone_number || null,
            display_name: data.display_name || null,
            is_ready: data.is_ready || false,
            is_suspended: data.is_suspended || false,
            message: data.message || '',
            requested_at: null,
            completed_at: null,
          })

          console.log('[Realtime] WhatsApp config updated:', data.setup_status)
        }
      },
      (error) => {
        console.error('[Realtime] Error listening to WhatsApp config:', error)
      }
    )

    return () => {
      console.log('[Realtime] Unsubscribed from WhatsApp config')
      unsubscribe()
    }
  }, [tenantId, enabled, queryClient])
}

/**
 * Listen to real-time payment transaction updates
 *
 * @param tenantId - Tenant ID to listen to
 * @param transactionId - Transaction ID to listen to
 * @param invoiceId - Invoice ID (for query key)
 * @param enabled - Whether to enable the listener (default: true)
 */
export function useRealtimePaymentTransaction(
  tenantId: string | null | undefined,
  transactionId: string | null | undefined,
  invoiceId: string | null | undefined,
  enabled = true
) {
  const queryClient = useQueryClient()

  useEffect(() => {
    const db = getFirestoreDb()
    if (!tenantId || !transactionId || !enabled || !db) return

    const docRef = doc(
      db,
      'tenants',
      tenantId,
      'payment_transactions',
      transactionId
    )

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()

          // Update the payment status query cache
          queryClient.setQueryData(['invoice-payment-status', invoiceId], {
            invoice_id: invoiceId,
            invoice_status: 'open', // Will be updated separately
            invoice_amount: data.amount || 0,
            payment_transaction: {
              id: data.id,
              status: data.status,
              amount: data.amount,
              provider: data.provider,
              provider_ref: data.provider_ref,
              external_ref: data.external_ref,
              failure_reason: data.failure_reason,
              created_at: null,
              completed_at: null,
            },
            message: getPaymentStatusMessage(data.status),
          })

          console.log('[Realtime] Payment transaction updated:', data.status)

          // If payment succeeded, also invalidate onboarding progress
          if (data.status === 'success') {
            queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] })
          }
        }
      },
      (error) => {
        console.error('[Realtime] Error listening to payment transaction:', error)
      }
    )

    return () => {
      console.log('[Realtime] Unsubscribed from payment transaction')
      unsubscribe()
    }
  }, [tenantId, transactionId, invoiceId, enabled, queryClient])
}

/**
 * Listen to real-time invoice status updates
 *
 * @param tenantId - Tenant ID to listen to
 * @param invoiceId - Invoice ID to listen to
 * @param enabled - Whether to enable the listener (default: true)
 */
export function useRealtimeInvoiceStatus(
  tenantId: string | null | undefined,
  invoiceId: string | null | undefined,
  enabled = true
) {
  const queryClient = useQueryClient()

  useEffect(() => {
    const db = getFirestoreDb()
    if (!tenantId || !invoiceId || !enabled || !db) return

    const docRef = doc(db, 'tenants', tenantId, 'payment_invoices', invoiceId)

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()

          console.log('[Realtime] Invoice status updated:', data.status)

          // If invoice is paid, invalidate onboarding progress
          if (data.status === 'paid') {
            queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] })
          }
        }
      },
      (error) => {
        console.error('[Realtime] Error listening to invoice status:', error)
      }
    )

    return () => {
      console.log('[Realtime] Unsubscribed from invoice status')
      unsubscribe()
    }
  }, [tenantId, invoiceId, enabled, queryClient])
}

// Helper function
function getPaymentStatusMessage(status: string): string {
  switch (status) {
    case 'success':
      return 'Payment successful!'
    case 'processing':
      return 'Processing your payment...'
    case 'failed':
      return 'Payment failed. Please try again.'
    case 'initiated':
      return 'Payment initiated...'
    default:
      return `Payment status: ${status}`
  }
}
